import * as Sentry from '@sentry/react'
import { Component, type ErrorInfo, type PropsWithChildren } from 'react'

type AppErrorBoundaryState =
  | { hasError: false; error: null }
  | { hasError: true; error: unknown }

export class AppErrorBoundary extends Component<
  PropsWithChildren,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { hasError: false, error: null }

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: unknown, errorInfo: ErrorInfo) {
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    })
    if (import.meta.env.DEV) console.error('Unhandled error', error, errorInfo)
  }

  render() {
    if (!this.state.hasError) return this.props.children

    return (
      <div style={{ padding: 24, maxWidth: 840, margin: '0 auto' }}>
        <h1 style={{ margin: 0, fontSize: 20 }}>Something went wrong</h1>
        <p style={{ opacity: 0.8 }}>
          The dashboard crashed unexpectedly. Try reloading the page.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="border border-border bg-secondary"
          style={{
            padding: '10px 14px',
            borderRadius: 10,
            color: 'inherit',
            cursor: 'pointer',
          }}
        >
          Reload
        </button>

        {import.meta.env.DEV ? (
          <pre
            className="bg-muted"
            style={{
              marginTop: 16,
              padding: 12,
              borderRadius: 10,
              overflow: 'auto',
            }}
          >
            {String(this.state.error)}
          </pre>
        ) : null}
      </div>
    )
  }
}
